import { useEffect } from 'react';
import { authService } from '../services/auth.service';
import { clearSessionAndRedirect } from '../services/api.client';

/**
 * Global idle-session watcher.
 *
 * After IDLE_LIMIT_MS of no user activity, the HRMS session is invalidated: tokens and
 * session data are cleared and the user is redirected to /login with an inactivity
 * message. Any user activity — mouse movement, clicks, keyboard input, scrolling, wheel,
 * touch, or a valid API interaction (see the axios request interceptor which dispatches
 * `app:activity`) — resets the timer.
 *
 * Only an authenticated user on a protected screen is watched; the login/platform/public
 * screens are ignored.
 */
// 20 minutes. Well inside the JWT's own 1440-minute life (JwtSettings.ExpirationMinutes), so this
// stays the binding limit — raising it further would eventually hand the cut-off to token expiry,
// which logs out with a different message and no inactivity explanation.
const IDLE_LIMIT_MS = 20 * 60 * 1000;
const RESET_THROTTLE_MS = 1000;      // coalesce high-frequency events (mousemove/scroll)

const PUBLIC_HASH_PREFIXES = ['#/login', '#/platform', '#/forgot', '#/reset', '#/activate', '#/license'];

const IdleTimeout: React.FC = () => {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastReset = 0;

    const isWatchedSession = (): boolean => {
      if (!authService.isAuthenticated()) return false;
      const hash = window.location.hash || '';
      return !PUBLIC_HASH_PREFIXES.some(p => hash.startsWith(p));
    };

    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };

    const onIdle = () => {
      if (isWatchedSession()) {
        // Clears authToken/user/context and redirects to /login?reason=idle_timeout.
        clearSessionAndRedirect('idle_timeout');
      }
    };

    const schedule = () => { clear(); timer = setTimeout(onIdle, IDLE_LIMIT_MS); };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastReset < RESET_THROTTLE_MS) return; // throttle bursty events
      lastReset = now;
      if (isWatchedSession()) schedule();
      else clear();
    };

    const domEvents: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'click', 'keydown', 'scroll', 'wheel', 'touchstart', 'touchmove',
    ];
    domEvents.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    // Valid API interactions (dispatched by the axios request interceptor).
    window.addEventListener('app:activity', onActivity);

    // Start the timer if a session is already active on mount.
    if (isWatchedSession()) schedule();

    return () => {
      clear();
      domEvents.forEach(e => window.removeEventListener(e, onActivity));
      window.removeEventListener('app:activity', onActivity);
    };
  }, []);

  return null;
};

export default IdleTimeout;
